import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Cross2Icon } from '@radix-ui/react-icons';
import * as React from 'react';

import { cn } from '@/utils/shadcn';
import { useKeyboardHeight } from '@/hooks/useKeyboardHeight';
import { PortalContainerContext } from '@/utils/getPortalContainer';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'g-fixed g-inset-0 g-z-50 g-bg-black/80 data-[state=open]:g-animate-in data-[state=closed]:g-animate-out data-[state=closed]:g-fade-out-0 data-[state=open]:g-fade-in-0',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  hideCloseButton?: boolean;
};

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, hideCloseButton = false, ...props }, ref) => {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'gbif g-fixed g-z-50 g-w-full g-border-solid g-bg-background g-shadow-lg',
          // g-left-[50%] paired with physical g-translate-x-[-50%] centers the dialog; switching to g-start-[50%] would break centering in RTL because translate-x stays physical
          'g-border g-left-[50%] g-top-[50%] g-grid g-max-w-lg g-translate-x-[-50%] g-translate-y-[-50%] g-gap-4 g-p-6 sm:g-rounded-lg',
          'g-duration-200 data-[state=open]:g-animate-in data-[state=closed]:g-animate-out data-[state=closed]:g-fade-out-0 data-[state=open]:g-fade-in-0 data-[state=closed]:g-zoom-out-95 data-[state=open]:g-zoom-in-95 data-[state=closed]:g-slide-out-to-left-1/2 data-[state=closed]:g-slide-out-to-top-[48%] data-[state=open]:g-slide-in-from-left-1/2 data-[state=open]:g-slide-in-from-top-[48%]',
          className
        )}
        {...props}
      >
        {children}
        {!hideCloseButton && (
          <DialogPrimitive.Close className="g-absolute g-end-4 g-top-4 g-rounded-sm g-opacity-70 g-ring-offset-background g-transition-opacity hover:g-opacity-100 focus:g-outline-none focus:g-ring-2 focus:g-ring-ring focus:g-ring-offset-2 disabled:g-pointer-events-none data-[state=open]:g-bg-accent data-[state=open]:g-text-muted-foreground">
            <Cross2Icon className="g-h-4 g-w-4" />
            <span className="g-sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogBottomSheetContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & { topOffset?: number }
>(({ className, children, style, topOffset = 75, ...props }, ref) => {
  const keyboardHeight = useKeyboardHeight();
  const [contentNode, setContentNode] = React.useState<HTMLDivElement | null>(null);
  const composedRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      setContentNode(node);
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
    },
    [ref]
  );

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={composedRef}
        className={cn(
          'gbif dialog-popover-container g-z-50 g-bg-background g-border-0 g-border-t g-border-solid',
          'g-fixed g-w-full g-start-0 g-end-0 g-bottom-auto',
          // Animations
          'g-duration-300 data-[state=open]:g-animate-in data-[state=closed]:g-animate-out data-[state=closed]:g-slide-out-to-bottom data-[state=open]:g-slide-in-from-bottom',
          className
        )}
        style={{
          ...style,
          top: topOffset,
          height: `calc(100dvh - ${topOffset + keyboardHeight}px)`,
          transition: keyboardHeight === 0 ? 'height 0.1s ease-out' : 'height 0.4s ease-out',
        }}
        {...props}
      >
        <PortalContainerContext.Provider value={contentNode}>
          {children}
        </PortalContainerContext.Provider>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogBottomSheetContent.displayName = 'DialogBottomSheetContent';

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('g-flex g-flex-col g-space-y-1.5 g-text-center sm:g-text-start', className)}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'g-flex g-flex-col-reverse sm:g-flex-row sm:g-justify-end sm:g-gap-2',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('g-text-lg g-font-semibold g-leading-none g-tracking-tight', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('g-text-sm g-text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogBottomSheetContent,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
